import { useContext, useMemo } from 'react';
import MantineTable from '../../../../common/table/mantine';
import moment from 'moment';
import { Button, NumberInput } from '@mantine/core';
import { CostingRequestsContext } from '../../../../contexts/CostingRequest';
import { getFooterInfoToExport } from './helper';
import { useAvic } from './context';

const AvicTable = ({
  data = [],
  loading,
  upload,
  footerData=[],
  setValues,
  setFooterValues,
  disableUpload,
  type
}) => {
  const { activePage } = useAvic();
  const { info } = useContext(CostingRequestsContext);
  const FooterData = Array.isArray(footerData) ? footerData : [];

  const handleChange = (value, name, plant) => {
    const index = data.findIndex((item) => item.open_plant === plant);
    const currentValues = [...data];
    currentValues[index] = { ...currentValues[index], [name]: value };
    setValues(currentValues);
  };

  const handleFooterChange = (value, name, index) => {
  const updatedFooterData = [...footerData];

  const currentItem = { ...updatedFooterData[index], [name]: value };
  // Recalculate final_avic
  const finalAvic = calculateFinalAvic(currentItem);
  currentItem.final_avic = finalAvic;

  updatedFooterData[index] = currentItem;
  setFooterValues(updatedFooterData);
};

  
  const calculateFinalAvic = (item) => {
    let { perf_plug = 0, price_plug = 0, manual_adjustment = 0, w_avg = 0 } = item || {};
    w_avg = w_avg === null || w_avg === undefined ? parseFloat(calculateWeightedAverage(item.production_country)) : w_avg;
    const total =
      parseFloat(w_avg) + Number(perf_plug) + Number(price_plug) + Number(manual_adjustment);
    return parseFloat(total || 0).toFixed(4);
  };

  const calculateTotalVolume = () => {
    return parseFloat(
      Object.values(data).reduce((sum, plant) => sum + Number(plant.volume), 0),
    ).toFixed(4);
  };

  const calculateTotalAvic = () => {
    return parseFloat(
      Object.values(data).reduce((sum, plant) => sum + plant.lvic * Number(plant.volume), 0),
    ).toFixed(4);
  };

  const calculateWeightedAverage = (item) => {
    if (item?.custom_wavg) {
      return item?.custom_wavg;
    } else {
      const totalVolume = calculateTotalVolume();
      const totalAvic = calculateTotalAvic();
      const wa = totalAvic / totalVolume ; 
      return parseFloat(wa || 0).toFixed(4);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'sku',
        header: 'SKU No.',
        Cell: () => info?.sku_data?.[0]?.sku || 'N/A',
      },
      {
        accessorKey: 'sku_description',
        header: 'SKU Desc.',
      },
      { accessorKey: 'open_plant', header: 'Production Plant' },
      {
        accessorKey: 'volume',
        header: 'Volume (HL)',
        Cell: ({ row }) => (
          <NumberInput
            value={row.original.volume}
            onChange={(value) => handleChange(value, 'volume', row.original.open_plant)}
            precision={4}
            min={0.01}
            step={0.01}
            max={10000000}
            disabled={disableUpload}
          />
        ),
      },
      { accessorKey: 'lvic', header: 'LVIC/HL' },
      {
        id: 'distribution_channel',
        header: 'Distribution Channel',
        footer: FooterData?.map((item, i) => (
          <div key={i} style={{ marginBottom: '20px' }}>
            {item.distribution_channel}
          </div>
        )),
      },
      {
        id: 'W.Avg VIC/HL',
        header: 'W.Avg VIC/HL',
        footer: (
          <>
            {FooterData?.map((item, i) => (
              <NumberInput
                key={i}
                value={
                  type === 'finalization'
                    ? item?.w_avg || ''
                    : item?.w_avg ?? parseFloat(calculateWeightedAverage(item.production_country))
                }
                onChange={(value) => handleFooterChange(value, 'w_avg', i)}
                precision={4}
                min={0}
                step={0.01}
                disabled={disableUpload}
                style={{ marginBottom: '6px' }}
              />
            ))}
          </>
        ),
      },
      {
        id: 'Perf Plug',
        header: 'Perf Plug',
        footer: FooterData?.map((item, i) => (
          <NumberInput
            key={i}
            value={item?.perf_plug || ''}
            onChange={(value) => handleFooterChange(value, 'perf_plug', i)}
            precision={4}
            min={-100}
            step={0.01}
            max={100}
            disabled={disableUpload}
            style={{ marginBottom: '6px' }}
          />
        )),
      },
      {
        id: 'Price Plug',
        header: 'Price Plug',
        footer: FooterData?.map((item, i) => (
          <NumberInput
            key={i}
            value={item?.price_plug || ''}
            onChange={(value) => handleFooterChange(value, 'price_plug', i)}
            precision={4}
            min={-100}
            step={0.01}
            max={100}
            disabled={disableUpload}
            style={{ marginBottom: '6px' }}
          />
        )),
      },
      {
        id: 'Other Adj',
        header: 'Other Adj',
        footer: FooterData?.map((item, i) => (
          <NumberInput
            key={i}
            value={item?.manual_adjustment || ''}
            onChange={(value) => handleFooterChange(value, 'manual_adjustment', i)}
            precision={4}
            min={-100}
            step={0.01}
            max={100}
            disabled={disableUpload}
            style={{ marginBottom: '6px' }}
          />
        )),
      },
      {
        id: 'Final AVIC/HL',
        header: 'Final AVIC/HL',
        footer: (
          <>
            {FooterData?.map((item, i) => (
              <div key={i} style={{ marginBottom: '20px' }}>
                {type === 'finalization' ? item?.final_avic || 'N/A' : calculateFinalAvic(item)}
              </div>
            ))}
          </>
        ),
      },
      {
        id: 'Ref SKU',
        header: 'Ref SKU',
        Cell: ({ row }) => <div>{row.original.mappedData?.ref_sku_number}</div>,
      },
      {
        id: 'Ref SKU Desc.',
        header: 'Ref SKU Desc.',
        Cell: ({ row }) => <div>{row.original.mappedData?.material?.[0]?.sku_description}</div>,
      },
      {
        id: 'Ref AVIC/HL',
        header: 'Ref AVIC/HL',
        Cell: ({ row }) => <div>{row.original.mappedData?.refSkuAvic}</div>,
      },
      {
        id: 'Curr',
        header: 'Curr',
        Cell: ({ row }) => <div>{row.original.mappedData?.ref_curr}</div>,
      },
      {
        id: activePage === 'avicSubmission' ? 'FP&A GCC SME' : 'FP&A Brewery Head',
        header: activePage === 'avicSubmission' ? 'FP&A GCC SME' : 'FP&A Brewery Head',
        footer: FooterData.updated_by,
      },
      {
        id: 'Submitted On',
        header: 'Submitted On',
        footer:
          FooterData.submitted_on && moment(FooterData?.submitted_on).isValid()
            ? moment(FooterData.submitted_on).format('YYYY-MM-DD')
            : 'N/A',
      },
    ],
    [data, FooterData, disableUpload],
  );

  const rightButtons = [
    {
      button: (
        <Button variant='gradient' color='dark' onClick={upload} disabled={disableUpload}>
          Upload Attachment
        </Button>
      ),
    },
  ];

  return (
    <MantineTable
      columns={columns}
      initialData={data}
      enableRowSelection={false}
      loading={loading}
      showSelectedToggle={false}
      hideSelectColumn
      showResetAll={false}
      pageSize={10}
      enableColumnFilter={false}
      enableFilters={false}
      enableExport
      columnFilterDisplayMode={false}
      rightButtons={rightButtons}
      csvFilename={`avic_${moment().format('YYYY-MM-DD')}`}
      className='avic-table'
      footerData={getFooterInfoToExport(Object.values(data), FooterData, info, activePage)}
    />
  );
};

export default AvicTable;
